import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

import { DEFAULT_LANGUAGE } from '../constants';
import { detectUserLanguage, saveLanguagePreference } from '../utils';

// 번역 리소스 직접 임포트 (초기 구현)
import ko from './locales/ko.json';
import en from './locales/en.json';
import zh from './locales/zh.json';
import ja from './locales/ja.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import hi from './locales/hi.json';
import de from './locales/de.json';

const resources = {
  en: { translation: en },
  es: { translation: es },
  de: { translation: de },
  fr: { translation: fr },
  zh: { translation: zh },
  ja: { translation: ja },
  ko: { translation: ko },
  hi: { translation: hi }
};

// i18n 초기화 상태 추적 (외부에서 접근 가능하도록)
let isInitialized = false;

// 초기화 완료를 확인하는 함수 (외부에서 사용 가능)
export const checkI18nInitialized = () => {
  return isInitialized && i18n.isInitialized;
};

// Capacitor 환경 감지 추가
const isCapacitor = !!(window.Capacitor || window.capacitor);

// Capacitor 전용 간단한 초기화 함수 추가
const initializeCapacitor = async () => {
  try {
    console.log('🔍 [i18n] Capacitor 환경 감지됨');
    
    // Capacitor에서는 간단한 언어 감지
    const capacitorLanguage = localStorage.getItem('i18nextLng') || DEFAULT_LANGUAGE;
    
    console.log(`✅ [i18n] Capacitor 언어: ${capacitorLanguage}`);
    
    // Capacitor 전용 동기식 초기화
    await i18n
      .use(initReactI18next)
      .init({
        resources,
        lng: capacitorLanguage,
        fallbackLng: DEFAULT_LANGUAGE,
        debug: false,
        interpolation: {
          escapeValue: false
        },
        defaultNS: 'translation',
        react: {
          useSuspense: false,
          transSupportBasicHtmlNodes: true,
          transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p']
        }
      });

    console.log(`✅ [i18n] Capacitor 초기화 완료: ${i18n.language}`);
    isInitialized = true;
    
  } catch (error) {
    console.error('❌ [i18n] Capacitor 초기화 실패:', error);
    
    // Capacitor 실패 시에도 간단한 설정
    await i18n
      .use(initReactI18next)
      .init({
        resources,
        lng: DEFAULT_LANGUAGE,
        fallbackLng: DEFAULT_LANGUAGE,
        debug: false,
        interpolation: {
          escapeValue: false
        },
        defaultNS: 'translation',
        react: {
          useSuspense: false
        }
      });
    isInitialized = true;
  }
};

// 커스텀 언어 감지 실행
const initializeLanguage = async () => {
  try {
    console.log('🔍 [i18n] 언어 감지 시작...');
    
    // 커스텀 언어 감지 로직 실행
    const detectedLanguage = await detectUserLanguage();
    
    console.log(`✅ [i18n] 감지된 언어: ${detectedLanguage}`);
    
    // i18next 초기화
    await i18n
      // Backend 플러그인 (나중에 동적 로딩 시 사용)
      .use(Backend)
      // 언어 감지 플러그인
      .use(LanguageDetector)
      // React와 연동
      .use(initReactI18next)
      // i18next 초기화
      .init({
        // 초기에는 리소스를 직접 설정 (안정성을 위해)
        resources,
        
        // 감지된 언어로 설정
        lng: detectedLanguage,
        fallbackLng: DEFAULT_LANGUAGE,
        
        // 언어 감지 설정 (우리가 이미 감지했으므로 비활성화)
        detection: {
          // 감지 순서: localStorage만 사용 (우리가 이미 감지했으므로)
          order: ['localStorage'],
          // localStorage 키 이름 (i18next 기본값 사용)
          lookupLocalStorage: 'i18nextLng',
          // 캐시 설정
          caches: ['localStorage']
        },
        
        // 개발 환경에서 디버그 모드
        debug: false,
        
        // 보간 설정
        interpolation: {
          escapeValue: false
        },
        
        // 네임스페이스 설정 (기본은 'translation' 사용)
        defaultNS: 'translation',
        
        // 로딩 실패 시 처리 - 개발 환경에서만 경고
        parseMissingKeyHandler: (key) => {
          // 빈 키나 undefined 키는 무시
          if (!key || key === 'undefined' || key === 'null') {
            return '';
          }
          if (process.env.NODE_ENV === 'development' && isInitialized) {
            console.warn(`🌐 [i18n] Missing key: ${key}`);
          }
          return key;
        },
        
        // missing key에 대한 더 세밀한 제어
        saveMissing: false,
        missingKeyHandler: false,
        
        // React에서 자주 발생하는 문제 방지
        react: {
          useSuspense: false,
          bindI18n: 'loaded languageChanged',
          bindI18nStore: 'added removed',
          transEmptyNodeValue: '',
          transSupportBasicHtmlNodes: true,
          transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p']
        }
      });

    console.log(`✅ [i18n] 초기화 완료: ${i18n.language}`);
    isInitialized = true;
    
  } catch (error) {
    console.error('❌ [i18n] 초기화 실패:', error);
    
    // 실패 시 기본 설정으로 초기화
    await i18n
      .use(Backend)
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        resources,
        lng: DEFAULT_LANGUAGE,
        fallbackLng: DEFAULT_LANGUAGE,
        detection: {
          order: ['localStorage', 'navigator'],
          lookupLocalStorage: 'i18nextLng',
          caches: ['localStorage']
        },
        debug: false,
        interpolation: {
          escapeValue: false
        },
        defaultNS: 'translation',
        parseMissingKeyHandler: (key) => {
          // 빈 키나 undefined 키는 무시
          if (!key || key === 'undefined' || key === 'null') {
            return '';
          }
          if (process.env.NODE_ENV === 'development' && isInitialized) {
            console.warn(`🌐 [i18n] Missing key: ${key}`);
          }
          return key;
        },
        saveMissing: false,
        missingKeyHandler: false,
        react: {
          useSuspense: false
        }
      });
    isInitialized = true;
  }
};

// 환경에 따른 분기 처리 추가
if (isCapacitor) {
  // Capacitor 환경: 간단한 초기화
  initializeCapacitor();
} else {
  // 웹 환경: 기존 로직 그대로 실행
  initializeLanguage();
}

// 언어 변경 이벤트 리스너 추가 (네이티브 저장소 동기화)
i18n.on('languageChanged', (lng) => {
  console.log(`🌐 [i18n] 언어 변경 감지: ${lng}`);
  // 네이티브 저장소에도 저장 (Share Extension 연동)
  saveLanguagePreference(lng);
});

// 개발 환경에서 디버깅을 위해 전역 객체에 노출
if (process.env.NODE_ENV === 'development') {
  window.i18n = i18n;
  console.log('🌍 [i18n] 개발 모드: window.i18n 전역 객체 생성');
}

export default i18n; 